import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  School,
  AccountCircle,
  Dashboard,
  Book,
  Language,
  Login,
  PersonAdd,
  Logout,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useWeb3 } from '../../contexts/Web3Context';
import LanguageSwitcher from '../LanguageSwitcher';

const Navbar: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { user, isAuthenticated, logout } = useAuth();
  const { account, connectWallet, disconnectWallet, isConnected } = useWeb3();
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    disconnectWallet();
    handleMenuClose();
    navigate('/');
  };

  const handleWeb3Connect = async () => {
    if (isConnected) {
      disconnectWallet();
    } else {
      await connectWallet();
    }
  };

  const menuItems = [
    { text: t('common.home'), path: '/', icon: <School /> },
    { text: t('common.courses'), path: '/courses', icon: <Book /> },
    { text: t('common.web3'), path: '/web3', icon: <Language /> },
  ];

  const authMenuItems = isAuthenticated
    ? [
        { text: t('common.dashboard'), path: '/dashboard', icon: <Dashboard /> },
        { text: t('common.profile'), path: '/profile', icon: <AccountCircle /> },
      ]
    : [
        { text: t('common.login'), path: '/login', icon: <Login /> },
        { text: t('common.register'), path: '/register', icon: <PersonAdd /> },
      ];

  const renderDesktopMenu = () => (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {menuItems.map((item) => (
          <Button
            key={item.text}
            color="inherit"
            component={Link}
            to={item.path}
            startIcon={item.icon}
          >
            {item.text}
          </Button>
        ))}
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* 语言切换器 */}
        <LanguageSwitcher />

        {/* Web3连接按钮 */}
        <Button
          variant={isConnected ? 'contained' : 'outlined'}
          color={isConnected ? 'success' : 'primary'}
          onClick={handleWeb3Connect}
          size="small"
        >
          {isConnected
            ? `${account?.slice(0, 6)}...${account?.slice(-4)}`
            : t('header.connectWallet')}
        </Button>

        {isAuthenticated ? (
          <>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                {user?.fullName?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              {authMenuItems.map((item) => (
                <MenuItem
                  key={item.text}
                  onClick={() => {
                    navigate(item.path);
                    handleMenuClose();
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  {item.text}
                </MenuItem>
              ))}
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <Logout />
                </ListItemIcon>
                {t('common.logout')}
              </MenuItem>
            </Menu>
          </>
        ) : (
          <>
            {authMenuItems.map((item) => (
              <Button
                key={item.text}
                color="inherit"
                component={Link}
                to={item.path}
                startIcon={item.icon}
              >
                {item.text}
              </Button>
            ))}
          </>
        )}
      </Box>
    </>
  );

  const renderMobileMenu = () => (
    <>
      <IconButton
        color="inherit"
        aria-label="open drawer"
        edge="start"
        onClick={() => setMobileDrawerOpen(true)}
      >
        <MenuIcon />
      </IconButton>

      <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
        {t('header.siteName')}
      </Typography>

      {isAuthenticated && (
        <IconButton
          size="large"
          aria-label="account of current user"
          onClick={handleMenuOpen}
          color="inherit"
        >
          <Avatar sx={{ width: 32, height: 32 }}>
            {user?.fullName?.charAt(0) || 'U'}
          </Avatar>
        </IconButton>
      )}

      <Drawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
      >
        <Box
          sx={{ width: 250 }}
          role="presentation"
          onClick={() => setMobileDrawerOpen(false)}
          onKeyDown={() => setMobileDrawerOpen(false)}
        >
          <List>
            {/* 语言切换器 - 移动端 */}
            <ListItem>
              <LanguageSwitcher />
            </ListItem>
            
            {menuItems.map((item) => (
              <ListItem key={item.text} component={Link} to={item.path}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            ))}
            
            {authMenuItems.map((item) => (
              <ListItem key={item.text} component={Link} to={item.path}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            ))}

            {/* Web3连接按钮 - 移动端 */}
            <ListItem>
              <Button
                variant={isConnected ? 'contained' : 'outlined'}
                color={isConnected ? 'success' : 'primary'}
                onClick={handleWeb3Connect}
                fullWidth
                startIcon={<Language />}
              >
                {isConnected
                  ? `${account?.slice(0, 6)}...${account?.slice(-4)}`
                  : t('header.connectWallet')}
              </Button>
            </ListItem>

            {isAuthenticated && (
              <ListItem onClick={handleLogout}>
                <ListItemIcon>
                  <Logout />
                </ListItemIcon>
                <ListItemText primary={t('common.logout')} />
              </ListItem>
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );

  return (
    <AppBar position="static">
      <Toolbar>
        <Typography
          variant="h6"
          component={Link}
          to="/"
          sx={{
            mr: 2,
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            color: 'inherit',
            fontWeight: 'bold',
          }}
        >
          <School sx={{ mr: 1 }} />
          {!isMobile && t('header.siteName')}
        </Typography>

        {isMobile ? renderMobileMenu() : renderDesktopMenu()}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar; 